import React, { SyntheticEvent, useMemo } from 'react'
import {
  DataGrid,
  GridCellParams,
  GridColumns,
  GridRowParams,
  GridValueGetterParams,
  MuiEvent,
} from '@material-ui/data-grid'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined'
import { Tooltip, useTheme } from '@material-ui/core'
import { StudySiteData } from '@models/data.models'
import ActionMenu from '../ActionMenu/ActionMenu'
import { StatusEnum, statusStyle } from '../Study/models'
import { EmptyInfoContainer } from '@components/EmptyInfoContainer'

interface SiteListProps {
  sites: Array<StudySiteData>
  onDelete: (id: string) => void
  onStudySiteClick: (id: string) => void
}

const siteStatusTitleText = (status: StatusEnum) => {
  switch (status) {
    case StatusEnum.CREATED:
      return 'Site needs to be customized'
    case StatusEnum.ERROR:
      return 'Site study has errors'
    default:
      return `Site status ${status}`
  }
}

export const SiteList: React.FunctionComponent<SiteListProps> = ({
  sites: sites,
  onStudySiteClick,
}) => {
  const theme = useTheme()
  const columnsDefinition = useMemo(() => {
    const columns: GridColumns = [
      {
        field: 'status',
        headerName: ' ',
        align: 'center',
        flex: 0.1,
        sortable: false,
        filterable: false,
        hideSortIcons: true,
        renderCell: (params: GridCellParams) => {
          const parsedSiteStatus = params.value as string
          const castedSiteStatus = parsedSiteStatus.toLowerCase() as StatusEnum

          switch (castedSiteStatus) {
            case StatusEnum.CREATED:
              return (
                <Tooltip
                  title={siteStatusTitleText(castedSiteStatus)}
                  placement={'top-start'}
                >
                  <ReportProblemOutlinedIcon
                    style={{
                      color: statusStyle(castedSiteStatus, theme).color,
                    }}
                  />
                </Tooltip>
              )
            case StatusEnum.ERROR:
              return (
                <Tooltip
                  title={siteStatusTitleText(castedSiteStatus)}
                  placement={'top-start'}
                >
                  <HighlightOffOutlinedIcon
                    style={{
                      color: statusStyle(castedSiteStatus, theme).color,
                    }}
                  />
                </Tooltip>
              )
            default:
              return ' '
          }
        },
      },
      {
        field: 'label',
        headerName: 'Site ID',
        flex: 0.7,
      },
      {
        field: 'region',
        headerName: 'Region',
        flex: 1,
      },
      {
        field: 'subjectCount',
        headerName: 'Subjects',
        flex: 0.3,
      },
      {
        field: 'id',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        hideSortIcons: true,
        flex: 0.2,
        valueGetter: (params: GridValueGetterParams) => {
          const actions = params.getValue(params.id, 'actions') as string[]
          if (!actions) return []
          return actions
        },
        renderCell: (params: GridCellParams) => (
          <ActionMenu
            id={params.id as string}
            menuOptions={params.value as string[]}
          />
        ),
      },
    ]

    return columns
  }, [theme])

  const handleStudySiteClick = (
    params: GridRowParams,
    _event: MuiEvent<SyntheticEvent<Element, Event>>
  ) => {
    onStudySiteClick(params.id as string)
  }

  if (sites.length === 0) {
    return <EmptyInfoContainer infoText="No Study Sites" />
  }

  return (
    <DataGrid
      rows={sites}
      columns={columnsDefinition}
      hideFooter
      density="compact"
      autoHeight
      onRowClick={handleStudySiteClick}
      style={{ backgroundColor: theme.palette.background.paper }}
    />
  )
}
